import { db } from "../../config/main_config";
import { getAssignees } from "../getAssignees.module";
import { waitForWindowProperty } from "./routerAdditives.util";

/**
 * Interface representing user data structure.
 */
interface UserData {
    firstName: string;
    lastName: string;
    [key: string]: any; // For any additional properties
}

/**
 * Interface representing an assignee.
 */
interface Assignee {
    userID: string;
    firstName: string;
    lastName: string;
    [key: string]: any; // For any additional properties
}

// Extend the Window interface to include assignees
declare global {
    interface Window {
        assignees?: any[];
    }
}

/**
 * Searches for a user's UID based on their full name or email, checking window.assignees and then the database.
 * @param nameOrEmail - The full name (e.g., "John Doe") or email of the user to look up.
 * @returns A promise that resolves to the user's UID or 'not_found' if not found.
 */
export const nameOrEmailToUID = async (nameOrEmail: string, secondAttempt: boolean = false): Promise<string> => {
    if (!nameOrEmail) return "unassigned";
    console.log(`[Utilities] (nameOrEmailToUID) Searching for UID by name/email: ${nameOrEmail}`);

    // Check window.assignees first
    if (window.assignees && window.assignees.length > 0) {
        for (let assignee of window.assignees) {
            // Check for full name match
            const fullName = `${assignee.firstName} ${assignee.lastName}`;
            if (fullName.toLowerCase() === nameOrEmail.toLowerCase() || 
                assignee.email?.toLowerCase() === nameOrEmail.toLowerCase()) {
                return assignee.userID;
            }
        }
    } else if (!secondAttempt) {
        // Wait for window.assignees to be available
        await waitForWindowProperty("assignees", async () => { }, async () => { await getAssignees(); });
        console.log(`[Utilities] (nameOrEmailToUID) window.assignees has been populated...`);
        return await nameOrEmailToUID(nameOrEmail, true);
    }

    // If not found in window.assignees, check the database
    return await checkDatabaseForNameOrEmail(nameOrEmail);
};

/**
 * Checks the database for a user with the given name or email and returns their UID.
 * @param nameOrEmail - The full name or email of the user to look up.
 * @returns A promise that resolves to the user's UID or 'not_found' if not found.
 */
const checkDatabaseForNameOrEmail = async (nameOrEmail: string): Promise<string> => {
    try {
        const usersRef = db.collection('users');
        const nameOrEmailLower = nameOrEmail.toLowerCase();

        // Query for matching full name or email
        const snapshot = await usersRef
            .where('email', '==', nameOrEmailLower)
            .get();

        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        }

        // If no email match, try name match
        const allUsers = await usersRef.get();
        for (const doc of allUsers.docs) {
            const data = doc.data() as UserData;
            const fullName = `${data.firstName} ${data.lastName}`.toLowerCase();
            if (fullName === nameOrEmailLower) {
                return doc.id;
            }
        }

        return 'not_found';
    } catch (error: any) {
        console.error(`[Utilities] (checkDatabaseForNameOrEmail) Error checking database: ${error.message}`, {
            nameOrEmail
        });
        return 'not_found';
    }
};